import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@radix-ui/react-select';
import { createColumnHelper } from '@tanstack/react-table';
import { Download, Info, PartyPopper, TrendingDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { SelectTrigger } from '~shared/shadcn/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

interface AttendanceDrilldownRow {
  id: string;
  className: string;
  instructorName: string;
  attendanceRate: number;
  changeVsLastMonth: number;
  enrolled: number;
  capacity: number;
  lastClassDate: string;
}

interface AttendanceDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeframe?: '7d' | '30d';
  viewType?: 'class' | 'instructor';
  data?: Array<AttendanceDrilldownRow>;
  averages?: {
    current: number;
    previous: number;
    delta: number;
    lowest10PctThreshold: number;
  };
}
const columnHelper = createColumnHelper<AttendanceDrilldownRow>();

// Mock data for demonstration
const mockData = [
  {
    id: '1',
    className: 'Ballet Tue 4:00 PM',
    instructorName: 'Hope M.',
    attendanceRate: 72,
    changeVsLastMonth: -12,
    enrolled: 9,
    capacity: 14,
    lastClassDate: 'Oct 11',
  },
  {
    id: '2',
    className: 'Hip Hop Wed 5:00 PM',
    instructorName: 'Jake L.',
    attendanceRate: 78,
    changeVsLastMonth: -7,
    enrolled: 11,
    capacity: 15,
    lastClassDate: 'Oct 10',
  },
  {
    id: '3',
    className: 'Jazz Thu 6:00 PM',
    instructorName: 'Abby R.',
    attendanceRate: 81,
    changeVsLastMonth: -5,
    enrolled: 12,
    capacity: 16,
    lastClassDate: 'Oct 9',
  },
  {
    id: '4',
    className: 'Contemporary Fri 5:30 PM',
    instructorName: 'Sarah K.',
    attendanceRate: 83,
    changeVsLastMonth: -3,
    enrolled: 10,
    capacity: 12,
    lastClassDate: 'Oct 11',
  },
  {
    id: '5',
    className: 'Tap Mon 3:00 PM',
    instructorName: 'Mike T.',
    attendanceRate: 85,
    changeVsLastMonth: -2,
    enrolled: 13,
    capacity: 16,
    lastClassDate: 'Oct 7',
  },
];

const mockAverages = {
  current: 94,
  previous: 91,
  delta: 3,
  lowest10PctThreshold: 82,
};

export function AttendanceDrilldownModal({
  isOpen,
  onClose,
  timeframe = '7d',
  viewType = 'class',
  data = mockData,
  averages = mockAverages,
}: AttendanceDrilldownModalProps) {
  const [selectedTimeframe, setSelectedTimeframe] = useState(timeframe);
  const [selectedViewType, setSelectedViewType] = useState(viewType);
  const columns = useMemo(
    () => [
      columnHelper.accessor('className', { header: 'Class' }),
      columnHelper.accessor('instructorName', { header: 'Instructor' }),
      columnHelper.accessor('attendanceRate', {
        header: 'Attendance %',
        cell: (info) => (
          <span className='font-semibold text-destructive'>
            {info.getValue()}%
          </span>
        ),
      }),
      columnHelper.accessor('changeVsLastMonth', {
        header: 'Change vs last month',
        cell: (info) => (
          <div className='flex items-center gap-1.5 text-destructive'>
            <TrendingDown className='size-3.5' />
            <span className='font-semibold'>{info.getValue()}%</span>
          </div>
        ),
      }),
      columnHelper.accessor(
        (row) => `${String(row.enrolled)} / ${String(row.capacity)}`,
        { id: 'capacity', header: 'Enrolled / capacity' },
      ),
      columnHelper.accessor('lastClassDate', { header: 'Last class date' }),
    ],
    [],
  );

  // Check if all classes are performing well (≥90%)
  const allClassesPerformingWell = data.every(
    (item) => item.attendanceRate >= 90,
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='flex max-h-[85vh] max-w-6xl flex-col overflow-hidden duration-300 animate-in fade-in-0 slide-in-from-bottom-4'>
        <DialogHeader className='border-b border-border/50 pb-4'>
          <DialogTitle className='text-2xl font-semibold text-[#000814]'>
            Low Attendance Classes
          </DialogTitle>
          <p className='mt-1 text-sm text-[#6B7280]'>
            Classes with lowest average attendance over the selected period.
          </p>
        </DialogHeader>

        {allClassesPerformingWell ? (
          // Empty state when all classes are performing well
          <div className='flex flex-1 flex-col'>
            <div className='flex flex-wrap items-center gap-3 border-b border-border/50 py-4'>
              <Select
                value={selectedTimeframe}
                onValueChange={(v) => {
                  setSelectedTimeframe(v as '7d' | '30d');
                }}
              >
                <SelectTrigger className='h-9 w-[140px] text-sm'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='7d'>Last 7 Days</SelectItem>
                  <SelectItem value='30d'>Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={selectedViewType}
                onValueChange={(v) => {
                  setSelectedViewType(v as 'class' | 'instructor');
                }}
              >
                <SelectTrigger className='h-9 w-[140px] text-sm'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='class'>By Class</SelectItem>
                  <SelectItem value='instructor'>By Instructor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='flex flex-1 flex-col items-center justify-center px-8 py-16 text-center'>
              <div className='mb-6 flex size-20 items-center justify-center rounded-full bg-yellow-50'>
                <PartyPopper className='size-10 text-[#FFC300]' />
              </div>
              <h3 className='mb-2 text-xl font-semibold text-[#000814]'>
                All classes are performing well!
              </h3>
              <p className='mb-6 max-w-md text-[#6B7280]'>
                No attendance concerns this week. All classes have 90% or higher
                attendance rates.
              </p>
              <Button variant='outline' className='bg-transparent'>
                View Full Attendance Report →
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Row */}
            <div className='grid grid-cols-1 gap-4 border-b border-border/50 py-4 sm:grid-cols-2 lg:grid-cols-3'>
              <div className='rounded-lg bg-[#F9FAFB] p-4'>
                <div className='mb-1 flex items-center gap-2'>
                  <p className='text-xs font-medium text-[#6B7280]'>
                    Average Attendance (This Period)
                  </p>
                </div>
                <p className='text-2xl font-bold tabular-nums text-[#000814]'>
                  {averages.current}%
                </p>
              </div>
              <div className='rounded-lg bg-[#F9FAFB] p-4'>
                <div className='mb-1 flex items-center gap-2'>
                  <p className='text-xs font-medium text-[#6B7280]'>
                    Average Attendance (Last Period)
                  </p>
                </div>
                <p className='text-2xl font-bold tabular-nums text-[#000814]'>
                  {averages.previous}%
                  <span className='ml-2 text-sm font-semibold text-[#16A34A]'>
                    +{averages.delta}%
                  </span>
                </p>
              </div>
              <div className='rounded-lg bg-[#F9FAFB] p-4'>
                <div className='mb-1 flex items-center gap-2'>
                  <p className='text-xs font-medium text-[#6B7280]'>
                    Lowest 10% of Classes
                  </p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className='text-[#6B7280] transition-colors hover:text-[#000814]'>
                          <Info className='size-3.5' />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className='max-w-xs rounded-xl shadow-lg'>
                        <p className='text-sm'>
                          Based on attendance logged in the past{' '}
                          {selectedTimeframe === '7d' ? '7' : '30'} days.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className='text-2xl font-bold tabular-nums text-[#DC2626]'>
                  ≤ {averages.lowest10PctThreshold}%
                </p>
              </div>
            </div>

            <EnrollioTable
              totalLabel='Low-attendance classes'
              filterHeader={(table) => (
                <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
                  <EnrollioTableSearch
                    table={table}
                    placeholder='Search classes or instructors...'
                  />
                  <Select
                    value={selectedTimeframe}
                    onValueChange={(v) => {
                      setSelectedTimeframe(v as '7d' | '30d');
                    }}
                  >
                    <SelectTrigger className='h-9 w-[140px] text-sm'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='7d'>Last 7 Days</SelectItem>
                      <SelectItem value='30d'>Last 30 Days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedViewType}
                    onValueChange={(v) => {
                      setSelectedViewType(v as 'class' | 'instructor');
                    }}
                  >
                    <SelectTrigger className='h-9 w-[140px] text-sm'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='class'>By Class</SelectItem>
                      <SelectItem value='instructor'>By Instructor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              toolbar={
                <Button
                  variant='ghost'
                  size='sm'
                  className='text-[#6B7280] hover:text-[#000814]'
                >
                  <Download className='mr-2 size-4' />
                  Export CSV
                </Button>
              }
              data={data}
              columns={columns}
              getRowId={(row) => row.id}
              emptyContent='No low-attendance classes.'
              ariaLabel='Low attendance classes'
            />
          </>
        )}

        {/* Footer */}
        <div
          className={`flex items-center border-t border-border/50 pt-4 ${
            allClassesPerformingWell ? 'justify-between' : 'justify-end'
          }`}
        >
          {allClassesPerformingWell ? (
            <Button
              variant='ghost'
              size='sm'
              className='text-[#6B7280] hover:text-[#000814]'
            >
              <Download className='mr-2 size-4' />
              Export CSV
            </Button>
          ) : null}
          <Button
            variant='outline'
            onClick={onClose}
            className='bg-transparent'
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
